/**
 * Core domain vocabulary for the QR customiser.
 *
 * Every style name is a string-literal union rather than a bare `string`, so a
 * typo in a component is a compile error instead of a silent fall-through to a
 * `default` branch at render time.
 */

export const ERROR_CORRECTION_LEVELS = ['L', 'M', 'Q', 'H'] as const;
export type ErrorCorrectionLevel = (typeof ERROR_CORRECTION_LEVELS)[number];

export const MODULE_STYLES = [
  'classic',
  'dotted',
  'rounded',
  'rounded_rect',
  'circle',
  'bubbles',
  'diamond',
  'classy',
  'classy_rounded',
  'hexagon',
  'star',
  'heart',
  'leaf',
  'triangle',
  'cross',
  'flower',
  'lightning',
  'waves',
] as const;
export type ModuleStyle = (typeof MODULE_STYLES)[number];

export const OUTER_EYE_STYLES = [
  'square',
  'rounded',
  'circle',
  'rounded_top_left',
  'rounded_top_right',
  'rounded_bottom_left',
  'rounded_bottom_right',
  'diagonal',
  'dots',
  'diamond',
  'flower_shape',
  'star_shape',
  'hexagon',
  'shield',
  'wave',
  'gear',
] as const;
export type OuterEyeStyle = (typeof OUTER_EYE_STYLES)[number];

export const INNER_EYE_STYLES = [
  'square',
  'rounded',
  'circle',
  'dots_3x3',
  'diamond',
  'leaf',
  'vertical_bars',
  'horizontal_bars',
  'star',
  'heart',
  'flower',
  'cross',
  'triangle',
  'hexagon',
  'wave',
  'lightning',
  'target',
] as const;
export type InnerEyeStyle = (typeof INNER_EYE_STYLES)[number];

/** A normalised 0..1 rounding strength for the eye styles that support it. */
export type BorderRadiusRatio = number;

/**
 * Everything that affects the rendered output. Deliberately serialisable except
 * for the two image slots, which the history layer copies by reference.
 */
export interface QrDesign {
  readonly content: string;
  readonly errorCorrectionLevel: ErrorCorrectionLevel;
  readonly size: number;

  readonly moduleStyle: ModuleStyle;
  readonly moduleColor: string;
  readonly backgroundColor: string;

  readonly outerEyeStyle: OuterEyeStyle;
  readonly innerEyeStyle: InnerEyeStyle;
  readonly eyeColor: string;
  readonly outerBorderRadius: BorderRadiusRatio;
  readonly innerBorderRadius: BorderRadiusRatio;
  readonly styleEyesAsModules: boolean;

  readonly centerImage: File | null;
  readonly centerImageSize: number;
  readonly customModuleImage: File | null;
}

/** A style option as rendered in a `<Select>`. */
export interface StyleOption<TValue extends string> {
  readonly value: TValue;
  readonly label: string;
  readonly description: string;
}
